import {
  ArmstrongLogo,
  ArmstrongLogoColor,
  ArmstrongWatermark,
  FirstOklahomaLogo,
  FirstOklahomaLogoColor,
  FlbLogo,
  FlbLogoColor,
  FlbWatermark,
  GreatPlainsLogo,
  GreatPlainsLogoColor,
  GreatPlainsWatermark,
  MntBannerWatermark,
  MntLogo,
  MntLogoColor,
  MntWatermark,
  UmbLogo,
  UmbLogoColor,
  WeokieBannerWatermark,
  WeokieLogo,
  WeokieLogoColor,
  WeokieWatermark,
} from "@assets/partners";
import { partnerBanks } from "@config/partnerBanks";
import {
  FeatureFlagValue,
  PartnerBank,
  PartnerBankKey,
  PartnerInstitutionKind,
} from "@ctypes";

// One entry per partner. Kept deliberately in the same shape as the registry in
// future-money-mobile so the two can be read side by side, and so the pair can
// later be hoisted into @futuremoney/common rather than diverging.
export const PARTNER_BANKS: Array<PartnerBank> = [
  {
    key: PartnerBankKey.Mnt,
    partnerId: partnerBanks.mnt.partnerId,
    featureFlag: FeatureFlagValue.MntEnabled,
    displayName: "M&T Bank",
    shortName: "M&T",
    accountName: "M&T Bank",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#007D56",
      gradientStartOffset: 0.2461,
      gradientEnd: "#05604A",
      gradientEndOffset: 0.9856,
      titleHighlight: "#F7B696",
      infoCard: "#018362",
      infoCardBorder: null,
      featureSubtitle: "rgba(255, 255, 255, 0.6)",
      motivational: "#006951",
      cta: "rgba(38, 255, 139, 0.2)",
      ctaText: "#FFFFFF",
      banner: "#017E57",
    },
    logos: {
      Light: MntLogo,
      Color: MntLogoColor,
      Watermark: MntWatermark,
      BannerWatermark: MntBannerWatermark,
      badgeHeight: 30,
      bannerHeight: 40,
    },
    watermarkStyle: {
      position: "absolute",
      top: 20,
      right: 0,
      bottom: 100,
      height: "90%",
      width: "80%",
    },
  },
  {
    key: PartnerBankKey.Flb,
    partnerId: partnerBanks.flb.partnerId,
    featureFlag: FeatureFlagValue.FlbEnabled,
    displayName: "First Liberty Bank",
    shortName: "FLB",
    accountName: "FLB",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#8A1D41",
      gradientStartOffset: 0.2461,
      gradientEnd: "#630D2E",
      gradientEndOffset: 0.9856,
      titleHighlight: "#F7B696",
      infoCard: "#8A1D41",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "rgba(255, 255, 255, 0.71)",
      motivational: "#7B1033",
      cta: "rgba(138, 29, 65, 0.4)",
      ctaText: "#FFFFFF",
      banner: "#8A1D41",
    },
    logos: {
      Light: FlbLogo,
      Color: FlbLogoColor,
      Watermark: FlbWatermark,
      badgeHeight: 28,
      bannerHeight: 45,
    },
    watermarkStyle: { position: "absolute", left: 0, bottom: "-12%", height: "62%", width: "142%" },
  },
  {
    key: PartnerBankKey.Armstrong,
    partnerId: partnerBanks.armstrong.partnerId,
    featureFlag: FeatureFlagValue.ArmstrongEnabled,
    displayName: "Armstrong Bank",
    shortName: "Armstrong",
    accountName: "Armstrong Bank",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#051F5D",
      gradientStartOffset: 0.6501,
      gradientEnd: "#1B4C7C",
      gradientEndOffset: 1,
      titleHighlight: "#99BFE5",
      infoCard: "#0D226D",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "#ACCBC5",
      motivational: "rgba(47, 102, 157, 0.3)",
      cta: "rgba(205, 0, 40, 0.7)",
      ctaText: "#FFFFFF",
      banner: "#002B5F",
    },
    logos: {
      Light: ArmstrongLogo,
      Color: ArmstrongLogoColor,
      Watermark: ArmstrongWatermark,
      badgeHeight: 34,
      bannerHeight: 74,
    },
    watermarkStyle: {
      position: "absolute",
      left: 0,
      top: "34%",
      height: "56%",
      width: "158%",
      opacity: 0.1,
    },
  },
  {
    key: PartnerBankKey.Umb,
    partnerId: partnerBanks.umb.partnerId,
    featureFlag: FeatureFlagValue.UmbEnabled,
    displayName: "UMB Bank",
    shortName: "UMB",
    accountName: "UMB",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#003369",
      gradientStartOffset: 0.7347,
      gradientEnd: "#00A2E6",
      gradientEndOffset: 0.9856,
      titleHighlight: "#00F3FF",
      infoCard: "#003369",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "#ACCBC5",
      motivational: "rgba(0, 162, 230, 0.6)",
      cta: "rgba(0, 51, 105, 0.4)",
      ctaText: "#FFFFFF",
      banner: "#003264",
    },
    logos: { Light: UmbLogo, Color: UmbLogoColor, badgeHeight: 25, bannerHeight: 25 },
  },
  {
    key: PartnerBankKey.GreatPlains,
    partnerId: partnerBanks.greatPlains.partnerId,
    featureFlag: FeatureFlagValue.GreatPlainsEnabled,
    displayName: "Great Plains Bank",
    shortName: "Great Plains",
    accountName: "Great Plains Bank",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#00355F",
      gradientStartOffset: 0,
      gradientEnd: "#08223C",
      gradientEndOffset: 0.4904,
      titleHighlight: "#CF8B35",
      infoCard: "rgba(207, 139, 53, 0.7)",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "rgba(255, 255, 255, 0.7)",
      motivational: "rgba(207, 139, 53, 0.6)",
      cta: "rgba(207, 139, 53, 0.7)",
      ctaText: "#FFFFFF",
      banner: "#103152",
    },
    logos: {
      Light: GreatPlainsLogo,
      Color: GreatPlainsLogoColor,
      Watermark: GreatPlainsWatermark,
      badgeHeight: 32,
      bannerHeight: 38,
    },
    watermarkStyle: {
      position: "absolute",
      left: "-9%",
      bottom: "9%",
      height: "17%",
      width: "118%",
    },
  },
  {
    key: PartnerBankKey.FirstOklahoma,
    partnerId: partnerBanks.firstOklahoma.partnerId,
    featureFlag: FeatureFlagValue.FirstOklahomaEnabled,
    displayName: "First Oklahoma Bank",
    shortName: "First Oklahoma",
    accountName: "First Oklahoma Bank",
    institutionKind: PartnerInstitutionKind.Bank,
    colors: {
      gradientStart: "#D41246",
      gradientStartOffset: 0.0673,
      gradientEnd: "#6E0F28",
      gradientEndOffset: 0.6243,
      titleHighlight: "#CBB778",
      infoCard: "rgba(212, 18, 70, 0.54)",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "rgba(255, 255, 255, 0.86)",
      motivational: "rgba(110, 16, 41, 0.85)",
      cta: "rgba(212, 18, 70, 0.32)",
      ctaText: "#FFFFFF",
      banner: "#D41246",
    },
    logos: {
      Light: FirstOklahomaLogo,
      Color: FirstOklahomaLogoColor,
      badgeHeight: 28,
      bannerHeight: 51,
    },
  },
  {
    key: PartnerBankKey.Weokie,
    partnerId: partnerBanks.weokie.partnerId,
    featureFlag: FeatureFlagValue.WeokieEnabled,
    displayName: "WEOKIE Credit Union",
    shortName: "WEOKIE",
    accountName: "WEOKIE",
    institutionKind: PartnerInstitutionKind.CreditUnion,
    colors: {
      gradientStart: "#0D2E4F",
      gradientStartOffset: 0,
      gradientEnd: "#003775",
      gradientEndOffset: 1,
      titleHighlight: "#F2861A",
      infoCard: "#0055B8",
      infoCardBorder: "#FFFFFF",
      featureSubtitle: "rgba(225, 238, 250, 0.85)",
      motivational: "rgba(106, 170, 228, 0.22)",
      cta: "#F2861A",
      ctaText: "#003775",
      banner: "#0055B8",
    },
    logos: {
      Light: WeokieLogo,
      Color: WeokieLogoColor,
      Watermark: WeokieWatermark,
      BannerWatermark: WeokieBannerWatermark,
      badgeHeight: 19,
      bannerHeight: 19,
    },
    watermarkStyle: {
      position: "absolute",
      left: "-37%",
      top: "46%",
      height: "42%",
      width: "189%",
    },
  },
];
